import { useCallback, useEffect, useState } from 'react';

// Components
import Alert from '../../../Alert';
import { useTranslation } from 'react-i18next';

// Queries
import getGrowingTogether from '../../../Queries/getGrowingTogether';

// Styles
import styles from '../index.module.sass';

// Images
import trophy from '../../../../images/consultancy/trofeo-bono.png';
import bonusCard from '../../../../images/consultancy/tarjeta-bono.png';

const isEmptyResponse = (response) => {
  if (response === null || response === undefined || response === '') {
    return true;
  }

  return (
    typeof response === 'object' &&
    !Array.isArray(response) &&
    Object.keys(response).length === 0
  );
};

const bonusLevels = [
  {
    level: 1,
    qualification: 'LGE',
  },
  {
    level: 2,
    qualification: 'LG2E',
  },
  {
    level: 3,
    qualification: 'LG3E',
  },
];

export default function Stage1({ getCookie, setSessionMessage }) {
  const { t } = useTranslation('growingtogether');

  const [open, setOpen] = useState(false);
  const [alertText, setAlertText] = useState('');
  const [loading, setLoading] = useState(true);
  const [bonusData, setBonusData] = useState(null);

  const getStage1 = useCallback(async () => {
    setLoading(true);

    // Para pruebas del WS puede usarse temporalmente:
    // const kit = 38187;

    const kit = getCookie('usuarioOk');

    const resp = await getGrowingTogether({
      tmbservice: 'GetBonoImpulsandoLideresEtapaUno',
      jsonBody: {
        kit,
      },
    });

    // Error técnico real
    if (resp?.typeMessage === 'error') {
      const message =
        resp?.message || t('queryError');

      setAlertText(message);
      setOpen(true);

      if (setSessionMessage) {
        setSessionMessage(message);
      }

      setBonusData(null);
      setLoading(false);
      return;
    }

    // 200 OK, pero el kit aún no tiene información de Etapa 1
    if (isEmptyResponse(resp)) {
      setBonusData({});
      setLoading(false);
      return;
    }

    setBonusData(resp?.data ?? resp);
    setLoading(false);
  }, [getCookie, setSessionMessage, t]);

  useEffect(() => {
    getStage1();
  }, [getStage1]);

  if (loading) {
    return (
      <div className={styles.stageMessage}>
        {t('loading')}
      </div>
    );
  }

  if (!bonusData) {
    return (
      <>
        <Alert
          open={open}
          setOpen={setOpen}
          title={t('attention')}
          text={alertText}
        />

        <div className={styles.stageMessage}>
          {t('noData')}
        </div>
      </>
    );
  }

  const bonuses = bonusLevels.map((bonus) => ({
    ...bonus,
    status: bonusData[`Bono${bonus.level}`],
    amount: bonusData[`MontoBono${bonus.level}`],
  }));

  const formatCurrency = (amount) =>
    Number(amount || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    });

  return (
    <div className={styles.stage1Container}>
      <Alert
        open={open}
        setOpen={setOpen}
        title={t('attention')}
        text={alertText}
      />

      {bonuses.map((bonus) => {
        const acquired =
          String(bonus.status || '')
            .trim()
            .toUpperCase() === 'APLICA';

        return (
          <section
            key={bonus.level}
            className={styles.bonusLevel}>
              <h2 className={styles.bonusLevelTitle}>
                {t(`stage1Level${bonus.level}`)}:
              </h2>

            <div
              className={`${styles.bonusCard} ${
                acquired ? styles.bonusCardAcquired : ''
              }`}>
              <div className={styles.bonusCardContent}>
                <p className={styles.bonusDescription}>
                  {t('qualificationText', {
                    qualification: bonus.qualification,
                  })}
                </p>
                {acquired ? (
                  <div className={styles.bonusAmount}>
                    {formatCurrency(bonus.amount)}
                  </div>
                ) : (
                  <p className={styles.bonusPending}>
                    {t('pending')}
                  </p>
                )}
              </div>

              <div className={styles.bonusCardVisual}>
                  <img
                    src={bonusCard}
                    alt=''
                    className={styles.cardImage}
                  />

                {acquired && (
                  <img
                    src={trophy}
                    alt={t('earnedBonus')}
                    className={styles.trophy}
                  />
                )}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}